/**
 * Shared Site Theme Configuration for Material-UI Components
 *
 * This theme configuration is synchronized with the Jekyll site's color scheme
 * defined in _config.yml and _sass/theme/_color-variables.scss.
 *
 * Features:
 * - Black/white/grey professional color palette
 * - Full light/dark mode support
 * - WCAG AA accessibility compliant (4.5:1+ contrast ratios)
 * - Consistent styling across all React components
 *
 * Usage:
 * import { getSiteTheme } from '../theme/siteTheme';
 * const theme = useMemo(() => getSiteTheme(isDarkMode), [isDarkMode]);
 *
 * Links inside rendered prose (HTML injected into a Box):
 * sx={{ '& a': (theme) => theme.mixins.linkProse }}
 *
 * Last Updated: 2026-10-16
 */

import { createTheme } from '@mui/material/styles';

/**
 * Text link colors. Mirrors _sass/theme/_link-tokens.scss;
 * src/__tests__/linkTokens.test.js keeps the two in sync.
 */
export const LINK_COLORS = {
  light: { main: '#0550ae', hover: '#033d8b', underline: 'rgba(5, 80, 174, 0.4)' },
  dark: { main: '#6cb6ff', hover: '#a5d6ff', underline: 'rgba(108, 182, 255, 0.4)' },
};

/**
 * Running-text link style, matching the Jekyll link-prose mixin.
 * @param {{main: string, hover: string, underline: string}} link
 */
const linkProse = (link) => ({
  color: link.main,
  textDecorationLine: 'underline',
  textDecorationThickness: '1px',
  textDecorationColor: link.underline,
  textUnderlineOffset: '0.22em',
  transition: 'color 0.15s ease, text-decoration-color 0.15s ease',
  '&:hover': {
    color: link.hover,
    textDecorationColor: 'currentColor',
    textDecorationThickness: '2px',
  },
  '&:focus-visible': {
    outline: `2px solid ${link.main}`,
    outlineOffset: '2px',
    borderRadius: '2px',
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
});

/**
 * Generate site theme based on dark mode state
 * @param {boolean} isDarkMode - Whether dark mode is enabled
 * @returns {Theme} Material-UI theme object
 */
const linkFor = (isDarkMode) => (isDarkMode ? LINK_COLORS.dark : LINK_COLORS.light);

export const getSiteTheme = (isDarkMode) => createTheme({
  mixins: {
    linkProse: linkProse(linkFor(isDarkMode)),
  },

  palette: {
    mode: isDarkMode ? 'dark' : 'light',

    // Primary color - used for main actions, emphasis
    primary: {
      main: isDarkMode ? '#ffffff' : '#212529',       // Pure white (dark) / Near black (light)
      light: isDarkMode ? '#e6e6e6' : '#343a40',      // Light grey (dark) / Lighter black (light)
      dark: isDarkMode ? '#d9d9d9' : '#1a1d20',       // Medium grey (dark) / Darker black (light)
      contrastText: isDarkMode ? '#0d1117' : '#ffffff', // Background color for contrast
    },

    // Secondary color - used for secondary actions
    secondary: {
      main: isDarkMode ? '#c9d1d9' : '#495057',       // Light grey (dark) / Dark grey (light)
      light: isDarkMode ? '#d9e1e9' : '#6c757d',      // Lighter grey (dark) / Medium grey (light)
      dark: isDarkMode ? '#8b949e' : '#343a40',       // Medium grey (dark) / Darker grey (light)
      contrastText: isDarkMode ? '#0d1117' : '#ffffff',
    },

    // Text link colors (LINK_COLORS): main = link (8.81:1 dark / 7.59:1 light), dark = hover
    link: {
      main: linkFor(isDarkMode).main,
      dark: linkFor(isDarkMode).hover,
      contrastText: isDarkMode ? '#0d1117' : '#ffffff',
    },

    // Background colors
    background: {
      default: isDarkMode ? '#0d1117' : '#ffffff',    // Primary background
      paper: isDarkMode ? '#161b22' : '#f8f9fa',      // Cards, modals, elevated surfaces
    },

    // Text colors
    text: {
      primary: isDarkMode ? '#f0f6fc' : '#212529',    // Primary text (14.89:1 / 16.02:1 contrast)
      secondary: isDarkMode ? '#c9d1d9' : '#495057',  // Secondary text (10.36:1 / 9.76:1 contrast)
      disabled: isDarkMode ? '#8b949e' : '#6c757d',   // Disabled text (5.86:1 / 5.63:1 contrast)
    },

    // Dividers and borders
    divider: isDarkMode ? '#30363d' : '#dee2e6',

    // Action colors (for interactive elements)
    action: {
      active: isDarkMode ? '#f0f6fc' : '#212529',
      hover: isDarkMode ? 'rgba(240, 246, 252, 0.08)' : 'rgba(33, 37, 41, 0.04)',
      selected: isDarkMode ? 'rgba(240, 246, 252, 0.16)' : 'rgba(33, 37, 41, 0.08)',
      disabled: isDarkMode ? 'rgba(139, 148, 158, 0.3)' : 'rgba(108, 117, 125, 0.3)',
      disabledBackground: isDarkMode ? 'rgba(139, 148, 158, 0.12)' : 'rgba(108, 117, 125, 0.12)',
    },
  },

  // Typography settings
  typography: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',

    // Header styles
    h1: {
      fontWeight: 600,
      fontSize: '2.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.5,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
    },

    // Body text
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.43,
    },

    // Button text
    button: {
      fontWeight: 600,
      textTransform: 'none', // Preserve case instead of uppercase
    },
  },

  // Shape settings
  shape: {
    borderRadius: 8, // Consistent border radius across components
  },

  // Component-specific style overrides
  components: {
    // Link styles: same running-text pattern as the Jekyll link-prose mixin.
    // MUI tints the underline with alpha(palette.link.main, 0.4).
    // Links that pass color="inherit" or another color keep that color.
    MuiLink: {
      defaultProps: {
        color: 'link',
        underline: 'always',
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          textDecorationThickness: '1px',
          textUnderlineOffset: '0.22em',
          transition: 'color 0.15s ease, text-decoration-color 0.15s ease',
          '&:hover': {
            textDecorationColor: 'currentColor',
            textDecorationThickness: '2px',
            ...(ownerState.color === 'link' && { color: theme.palette.link.dark }),
          },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.link.main}`,
            outlineOffset: '2px',
            borderRadius: '2px',
          },
        }),
      },
    },

    // Button styles
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 8,
          padding: '8px 16px',
          transition: 'all 0.2s ease-in-out',
        },
        contained: {
          backgroundColor: isDarkMode ? '#ffffff' : '#212529',
          color: isDarkMode ? '#0d1117' : '#ffffff',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: isDarkMode ? '#e6e6e6' : '#343a40',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          },
        },
        outlined: {
          borderColor: isDarkMode ? '#30363d' : '#dee2e6',
          '&:hover': {
            borderColor: isDarkMode ? '#ffffff' : '#212529',
            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(33, 37, 41, 0.05)',
          },
        },
        text: {
          '&:hover': {
            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(33, 37, 41, 0.04)',
          },
        },
      },
    },

    // Chip styles
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 6,
        },
        filled: {
          backgroundColor: isDarkMode ? '#21262d' : '#e9ecef',
          color: isDarkMode ? '#f0f6fc' : '#212529',
        },
        outlined: {
          borderColor: isDarkMode ? '#30363d' : '#dee2e6',
          backgroundColor: 'transparent',
        },
      },
    },

    // Card styles
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: isDarkMode ? '#161b22' : '#f8f9fa',
          borderRadius: 12,
          border: `1px solid ${isDarkMode ? '#30363d' : '#dee2e6'}`,
          boxShadow: 'none',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: isDarkMode ? '#8b949e' : '#6c757d',
          },
        },
      },
    },

    // TextField styles
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: isDarkMode ? '#161b22' : '#ffffff',
            '& fieldset': {
              borderColor: isDarkMode ? '#30363d' : '#dee2e6',
            },
            '&:hover fieldset': {
              borderColor: isDarkMode ? '#8b949e' : '#6c757d',
            },
            '&.Mui-focused fieldset': {
              borderColor: isDarkMode ? '#ffffff' : '#212529',
            },
          },
        },
      },
    },

    // Paper styles
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: isDarkMode ? '#161b22' : '#f8f9fa',
          backgroundImage: 'none', // Remove default gradient
        },
      },
    },

    // MenuItem styles
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: isDarkMode ? '#21262d' : '#e9ecef',
          },
          '&.Mui-selected': {
            backgroundColor: isDarkMode ? '#21262d' : '#e9ecef',
            '&:hover': {
              backgroundColor: isDarkMode ? '#30363d' : '#dee2e6',
            },
          },
        },
      },
    },

    // Divider styles
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: isDarkMode ? '#30363d' : '#dee2e6',
        },
      },
    },
  },
});

/**
 * Color palette reference for documentation
 *
 * Light Mode:
 * - Background: #ffffff, #f8f9fa, #e9ecef
 * - Text: #212529 (16.02:1), #495057 (9.76:1), #6c757d (5.63:1)
 * - Primary: #212529
 * - Border: #dee2e6
 *
 * Dark Mode:
 * - Background: #0d1117, #161b22, #21262d
 * - Text: #f0f6fc (14.89:1), #c9d1d9 (10.36:1), #8b949e (5.86:1)
 * - Primary: #ffffff
 * - Border: #30363d
 *
 * All contrast ratios in parentheses exceed WCAG AA standards (4.5:1 minimum)
 */
